import Image from 'next/image';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { AppointmentCtaSectionDTO } from '@lib/strapi/dtos/components/sections/appointmentCtaSection.dto';
import { Prose, openAppointmentModal } from '../shared-ui';
import PhoneLink from '@components/utils/phone-link';
import useTranslation from 'next-translate/useTranslation';

/**
 * The closing ask, in two editor-chosen appearances (owner, 2026-09-06 — the
 * `appearance` field, never adjacency detection):
 *
 * BRIGHT (default) — a boxed rounded panel on the page ground, for the
 * above-the-footer slot: the old full violet band sat directly against the
 * violet footer and the two read as one mass. The stamp appears here as a
 * real full-colour element, fully inside the panel at the left of the text.
 *
 * DARK — the artifact's m-cta band, kept for mid-page placements: full-bleed
 * violet-700, white text (8.62, AAA), the stamp as a faint clipped watermark
 * top-right (per .m-ctastamp), white phone pill.
 *
 * Composition follows the artifact: ONE action, the phone pill with icon and
 * number (style=Phone). The «Κλείστε ραντεβού» popup button renders only if
 * an editor sets style to Form/Both — the header and floating button already
 * carry that flow (D47).
 */
const AppointmentCtaSectionImplementation = ({ section }: ComponentProps<AppointmentCtaSectionDTO>) => {
  const { t } = useTranslation('main');
  if (!section) return null;
  const showPhone = section.style !== 'Form';
  const showForm = section.style !== 'Phone';
  const dark = section.appearance === 'Dark';

  const bookButton = (cls: string) =>
    showForm ? (
      <button type="button" onClick={() => openAppointmentModal('cta-section')} className={cls}>
        {t('cta.bookAppointment')}
      </button>
    ) : null;

  if (dark) {
    return (
      // .bleed: the dark band must span the viewport even when it renders
      // inside the boxed content column (e.g. mid-page on the homepage) —
      // same band language as the stats section. Harmless in full-width
      // zones, where 100vw centered equals the natural width.
      <section className="bleed relative my-14 overflow-hidden bg-violet-700 py-10 text-white md:my-20 md:py-14">
        {/* brush gesture, white at whisper opacity in the content-free left
            edge (clipped corners are the brush's normal habitat) */}
        <img loading="lazy" src="/images/brand/stroke-2.svg" alt="" aria-hidden="true"
          className="pointer-events-none absolute -left-12 -bottom-14 w-64 opacity-10 brightness-0 invert select-none" />
        <div className="shell relative flex flex-wrap items-center justify-between gap-6">
          <div className="flex min-w-0 items-center gap-6">
            {/* the stamp, FULLY visible (owner, 2026-09-07 — no more clipped
                corner watermark): a real element of the row, white on violet,
                sized to the band */}
            {/* the kit's MONO stamp exists exactly for one-colour grounds —
                lilac on violet-700, no brightness/invert hacks */}
            {/* width/height = intrinsic ratio (viewBox 332.7x343.2) so the
                browser reserves space before the SVG loads — this is the one
                IN-FLOW brand img; the absolute-positioned strokes cannot
                shift layout (Lighthouse CLS diagnostic, 2026-09-16) */}
            <img loading="lazy" src="/images/brand/stamp-mono.svg" alt="" aria-hidden="true"
              width={333} height={343}
              className="hidden h-24 w-auto flex-none select-none md:block" />
            <div className="max-w-xl">
              {section.heading ? <h2 className="text-2xl font-extrabold text-white md:text-3xl">{section.heading}</h2> : null}
              {section.prompt ? (
                <div className="mt-2 opacity-90 [&_.prose-content]:max-w-none [&_p]:mb-0">
                  <Prose content={section.prompt} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {bookButton('inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-extrabold text-violet-700 transition-colors hover:bg-violet-050')}
            {showPhone ? <PhoneLink variant="inverse" placement="cta-band" /> : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-2 pb-12 md:pt-4 md:pb-20">
      <div className="shell">
        {/* relative+overflow-hidden: hosts the brush gesture in the corner —
            the artifact's .panel-brush (bottom-right, half-clipped). The one
            place the identity's hand-drawn stroke appears per band. */}
        <div className="relative flex flex-wrap items-center justify-between gap-6 overflow-hidden rounded-3xl border border-violet-200 bg-violet-050 px-7 py-8 md:px-10">
          <img loading="lazy" src="/images/brand/stroke-4.svg" alt="" aria-hidden="true"
            className="pointer-events-none absolute -bottom-12 -right-8 w-56 opacity-25 select-none" />
          <div className="flex items-center gap-6">
            <Image src="/images/brand/stamp-color.svg" alt="" aria-hidden="true"
              width={82} height={85} className="hidden w-20 flex-none select-none sm:block" />
            <div className="max-w-xl">
              {section.heading ? <h2 className="text-2xl font-extrabold text-ink md:text-3xl">{section.heading}</h2> : null}
              {section.prompt ? (
                <div className="mt-2 text-muted [&_.prose-content]:max-w-none [&_p]:mb-0">
                  <Prose content={section.prompt} />
                </div>
              ) : null}
            </div>
          </div>
          {/* relative + opaque outline fill: the scribble stroke lives in
              this corner, and a transparent pill let it show through the
              button face (owner, 2026-09-08) */}
          <div className="relative flex flex-wrap items-center gap-3">
            {bookButton('inline-flex items-center gap-2 rounded-full border-2 border-violet-700 bg-violet-050 px-6 py-3 font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white')}
            {showPhone ? <PhoneLink variant="primary" placement="cta-band" /> : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentCtaSectionImplementation;
